import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { withRetry } from '../../utils/http';
import '../admin-sb.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function ArrowIcon({ s = 12 }) {
  return (
    <svg viewBox="0 0 14 14" width={s} height={s} fill="none" stroke="currentColor"
         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7h8M8 4l3 3-3 3" />
    </svg>
  );
}

export default function AdminLogin() {
  const { login } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(localStorage.getItem('sb-remember') !== 'false');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState(null); // { facilities, courts, team }
  const [waking, setWaking] = useState(false); // true while a cold-started backend is spinning up

  const isDark = theme === 'dark';
  const palette = localStorage.getItem('sb-palette') || 'warm';
  const density = localStorage.getItem('sb-density') || 'regular';

  // Live counts from the public (unauthenticated) stats endpoint. This is the
  // first call on a fresh load, so it's where a scale-to-zero cold start shows up
  // — retry with backoff and surface a "waking up" hint instead of a hard error.
  useEffect(() => {
    let alive = true;
    withRetry(
      () => axios.get(`${API_BASE}/api/stats/public`),
      { onRetry: () => { if (alive) setWaking(true); } },
    )
      .then(res => { if (alive) { setCounts(res.data); setWaking(false); } })
      .catch(() => { if (alive) setWaking(false); /* panel falls back to em-dashes */ });
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || loading) return;
    setError(null);
    setLoading(true);
    try {
      await login(username.trim(), password, remember, { onRetry: () => setWaking(true) });
      navigate('/admin');
    } catch (err) {
      // Real backend message (e.g. "Invalid username or password")
      setError(err.response?.data?.detail || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
      setWaking(false);
    }
  };

  const stat = (n) => (counts ? counts[n] : '—');

  return (
    <div className="sb-admin" data-mode={isDark ? 'dark' : 'light'} data-theme={palette} data-density={density}>
      <div style={{
        minHeight: '100vh', background: 'var(--bg)', display: 'grid', placeItems: 'center',
        padding: 24, position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative corner glow */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(circle at 0% 0%, oklch(0.92 0.04 85 / 0.5), transparent 35%), radial-gradient(circle at 100% 100%, oklch(0.85 0.06 155 / 0.18), transparent 40%)',
        }} />

        <div className="sb-login-card" style={{
          position: 'relative', width: '100%', maxWidth: 880,
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 20, boxShadow: 'var(--sh-2)', overflow: 'hidden',
        }}>
          {/* LEFT — brand panel */}
          <div className="sb-login-brand" style={{
            padding: '40px 40px 32px', background: 'var(--brand)', color: 'var(--brand-ink)',
            display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.08,
              backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 16px, currentColor 16px 17px)',
            }} />

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, background: 'var(--brand-ink)', color: 'var(--brand)',
                display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 16, letterSpacing: '.02em',
              }}>SB</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.015em' }}>Sport Bloc</div>
                <div style={{ fontSize: 11, opacity: 0.75, letterSpacing: '.14em', textTransform: 'uppercase' }}>Operator Console</div>
              </div>
            </div>

            <div className="sb-login-pitch" style={{ position: 'relative', marginTop: 'auto', paddingTop: 60 }}>
              <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1.15, maxWidth: 320 }}>
                Manage every court, every booking, every facility — from one place.
              </div>
              <div style={{ marginTop: 16, fontSize: 12.5, opacity: 0.8, lineHeight: 1.5, maxWidth: 320 }}>
                Real-time slot availability · recurring schedules · equipment rentals · refund tracking.
              </div>

              <div style={{ marginTop: 28, display: 'flex', gap: 18, fontSize: 11, opacity: 0.82,
                  letterSpacing: '.12em', textTransform: 'uppercase' }}>
                {[['facilities', 'facilities'], ['courts', 'courts'], ['team', 'team']].map(([key, label]) => (
                  <div key={key}>
                    <div className="sb-mono" style={{ fontSize: 22, letterSpacing: '-.02em', opacity: 1, fontWeight: 500 }}>
                      {stat(key)}
                    </div>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — form */}
          <form onSubmit={submit} style={{ padding: '40px 40px 36px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.02em', color: 'var(--ink)' }}>Sign in</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
                Welcome back. Use your operator credentials below.
              </div>
            </div>

            <div className="sb-field">
              <label>Username</label>
              <input value={username} onChange={(e) => { setUsername(e.target.value); setError(null); }}
                     autoFocus autoComplete="username" placeholder="e.g. admin" />
            </div>

            <div className="sb-field">
              <label>Password</label>
              <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(null); }}
                     autoComplete="current-password" placeholder="••••••••" />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                       style={{ accentColor: 'var(--brand)' }} />
                Remember me
              </label>
              <a href="#" onClick={(e) => e.preventDefault()}
                 style={{ fontSize: 12.5, color: 'var(--brand)', textDecoration: 'none' }}>
                Forgot password?
              </a>
            </div>

            {error && (
              <div style={{ padding: '10px 12px', background: 'var(--bad-bg)', color: 'var(--bad-fg)', borderRadius: 8, fontSize: 12.5 }}>
                {error}
              </div>
            )}

            {waking && !error && (
              <div style={{ padding: '10px 12px', background: 'var(--surface-2)', color: 'var(--ink-2)', borderRadius: 8, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="sb-spin" style={{ width: 12, height: 12, border: '2px solid var(--ink-4)', borderTopColor: 'var(--brand)', borderRadius: '50%', display: 'inline-block' }} />
                Waking up the server… first request after idle can take a few seconds.
              </div>
            )}

            <button type="submit" className="sb-btn brand" disabled={!username.trim() || loading}
                    style={{ width: '100%', justifyContent: 'center', padding: '12px 14px', fontSize: 14,
                        opacity: (!username.trim() || loading) ? 0.55 : 1 }}>
              {loading ? 'Signing in…' : 'Sign in'}
              {!loading && <ArrowIcon s={12} />}
            </button>

            {/* Demo-account quick-sign-in: intentionally omitted in production.
                The reference's seed users don't exist against real password auth
                (users are owner-created and not listable pre-auth). */}

            <div style={{ textAlign: 'center', marginTop: 2 }}>
              <button type="button" onClick={toggleTheme} className="sb-btn ghost sm" style={{ color: 'var(--ink-3)' }}>
                {isDark ? '☀ Light mode' : '☽ Dark mode'}
              </button>
            </div>

            <div style={{ fontSize: 11, color: 'var(--ink-4)', textAlign: 'center', marginTop: 4 }}>
              Sport Bloc · v2.4 · {new Date().getFullYear()}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
